import React, { createContext, useContext, useState, useEffect } from 'react';
import { crossProviderDoctorAPI } from '../services/api';

const DoctorViewContext = createContext();

export const useDoctorView = () => {
  const context = useContext(DoctorViewContext);
  if (!context) {
    throw new Error('useDoctorView must be used within a DoctorViewProvider');
  }
  return context;
};

export const DoctorViewProvider = ({ children }) => {
  const [currentView, setCurrentView] = useState('service_provider');
  const [doctorData, setDoctorData] = useState(null);
  const [doctorEmail, setDoctorEmail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load saved view preference
  useEffect(() => {
    const savedView = localStorage.getItem('current_view');
    if (savedView && (savedView === 'service_provider' || savedView === 'doctor')) {
      setCurrentView(savedView);
    }
  }, []);

  // Fetch doctor data when switching to doctor view
  const fetchDoctorData = async (userEmail) => {
    if (!userEmail) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const doctorDetails = await crossProviderDoctorAPI.getDoctorByEmail(userEmail);
      setDoctorData(doctorDetails);
    } catch (err) {
      console.error('Error fetching doctor data:', err);
      setError('Failed to load doctor data');
    } finally {
      setLoading(false);
    }
  };

  const switchToDoctorView = (userEmail) => {
    setCurrentView('doctor');
    setDoctorEmail(userEmail);
    localStorage.setItem('current_view', 'doctor');
    if (userEmail) {
      fetchDoctorData(userEmail);
    }
  };

  const switchToServiceProviderView = () => {
    setCurrentView('service_provider');
    setDoctorEmail(null);
    localStorage.setItem('current_view', 'service_provider');
    setDoctorData(null);
  };

  const value = {
    currentView,
    doctorData,
    doctorEmail,
    loading,
    error,
    switchToDoctorView,
    switchToServiceProviderView,
    fetchDoctorData,
    isDoctorView: currentView === 'doctor',
    isServiceProviderView: currentView === 'service_provider'
  };

  return (
    <DoctorViewContext.Provider value={value}>
      {children}
    </DoctorViewContext.Provider>
  );
};
